import type { SyncProvider, SyncItem, SyncResult, SyncStatus, SyncedItem } from "../types.js";

const LINEAR_API = "https://api.linear.app/graphql";

const DEFAULT_STATUS_MAP: Record<string, SyncStatus> = {
  Backlog: "backlog",
  Todo: "todo",
  "In Progress": "in_progress",
  "In Review": "review",
  Done: "done",
  Cancelled: "cancelled",
  Canceled: "cancelled",
};

const REVERSE_STATUS_MAP: Record<SyncStatus, string> = {
  backlog: "Backlog",
  todo: "Todo",
  in_progress: "In Progress",
  review: "In Review",
  done: "Done",
  cancelled: "Cancelled",
};

function buildDescription(item: SyncItem): string {
  return `${item.description}

---
_Synced from Fathom_
**Complexity:** ${item.complexity} | **Priority:** ${item.priority} | **Est. Cost:** $${item.estimatedCost.toFixed(2)}
**Acceptance Criteria:** ${item.acceptanceCriteria}
---`;
}

async function linearQuery(
  apiKey: string,
  query: string,
  variables?: Record<string, unknown>,
): Promise<Record<string, unknown>> {
  const res = await fetch(LINEAR_API, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: apiKey,
    },
    body: JSON.stringify({ query, variables }),
  });

  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Linear API error (${res.status}): ${text}`);
  }

  const json = (await res.json()) as { data?: Record<string, unknown>; errors?: Array<{ message: string }> };
  if (json.errors?.length) {
    throw new Error(`Linear GraphQL error: ${json.errors[0].message}`);
  }
  return json.data as Record<string, unknown>;
}

export function createLinearProvider(
  apiKey: string,
  projectKey: string,
  statusMapping?: Record<string, SyncStatus>,
): SyncProvider {
  const statusMap = { ...DEFAULT_STATUS_MAP, ...statusMapping };

  function mapToFathomStatus(linearState: string): SyncStatus {
    return statusMap[linearState] ?? "backlog";
  }

  return {
    name: "linear",

    async createItem(item: SyncItem): Promise<SyncResult> {
      // First, find the team by key
      const teamData = await linearQuery(apiKey, `
        query GetTeam($key: String!) {
          teams(filter: { key: { eq: $key } }) {
            nodes { id key }
          }
        }
      `, { key: projectKey });

      const teams = teamData.teams as { nodes: Array<{ id: string; key: string }> };
      if (!teams.nodes.length) {
        throw new Error(`Linear team not found with key: ${projectKey}`);
      }
      const teamId = teams.nodes[0].id;

      // Create the issue
      const labels = [...item.labels, "fathom", `complexity:${item.complexity}`, `priority:${item.priority}`];
      const description = buildDescription(item);

      const createData = await linearQuery(apiKey, `
        mutation CreateIssue($input: IssueCreateInput!) {
          issueCreate(input: $input) {
            success
            issue {
              id
              identifier
              url
            }
          }
        }
      `, {
        input: {
          teamId,
          title: item.title,
          description,
          labelIds: [], // Labels need to be created/resolved separately
        },
      });

      const result = createData.issueCreate as {
        success: boolean;
        issue: { id: string; identifier: string; url: string };
      };

      if (!result.success) {
        throw new Error("Failed to create Linear issue");
      }

      return {
        externalId: result.issue.id,
        url: result.issue.url,
        provider: "linear",
      };
    },

    async updateStatus(externalId: string, status: SyncStatus): Promise<void> {
      // Get workflow states for the issue's team
      const issueData = await linearQuery(apiKey, `
        query GetIssue($id: String!) {
          issue(id: $id) {
            team { id }
          }
        }
      `, { id: externalId });

      const issue = issueData.issue as { team: { id: string } };

      const statesData = await linearQuery(apiKey, `
        query GetStates($teamId: String!) {
          workflowStates(filter: { team: { id: { eq: $teamId } } }) {
            nodes { id name }
          }
        }
      `, { teamId: issue.team.id });

      const states = statesData.workflowStates as { nodes: Array<{ id: string; name: string }> };
      const targetName = REVERSE_STATUS_MAP[status] ?? "Backlog";
      const targetState = states.nodes.find((s) => s.name === targetName);

      if (!targetState) {
        throw new Error(`Linear workflow state not found: ${targetName}`);
      }

      await linearQuery(apiKey, `
        mutation UpdateIssue($id: String!, $input: IssueUpdateInput!) {
          issueUpdate(id: $id, input: $input) {
            success
          }
        }
      `, { id: externalId, input: { stateId: targetState.id } });
    },

    async getStatus(externalId: string): Promise<SyncStatus> {
      const data = await linearQuery(apiKey, `
        query GetIssueStatus($id: String!) {
          issue(id: $id) {
            state { name }
          }
        }
      `, { id: externalId });

      const issue = data.issue as { state: { name: string } };
      return mapToFathomStatus(issue.state.name);
    },

    async listItems(projectKey: string): Promise<SyncedItem[]> {
      const data = await linearQuery(apiKey, `
        query ListIssues($teamKey: String!) {
          issues(filter: {
            team: { key: { eq: $teamKey } },
            description: { contains: "Synced from Fathom" }
          }) {
            nodes {
              id
              identifier
              url
              description
              state { name }
              updatedAt
            }
          }
        }
      `, { teamKey: projectKey });

      const issues = data.issues as {
        nodes: Array<{
          id: string;
          identifier: string;
          url: string;
          description: string;
          state: { name: string };
          updatedAt: string;
        }>;
      };

      return issues.nodes.map((issue) => ({
        sliceId: issue.identifier, // Best effort — real mapping is in ledger
        externalId: issue.id,
        url: issue.url,
        status: mapToFathomStatus(issue.state.name),
        lastSynced: new Date(issue.updatedAt).getTime(),
      }));
    },
  };
}
